import React, { useEffect, useState } from "react";
import { Briefcase, Lock, ChevronDown, Plus, X } from "lucide-react";
import { Card, Input, Toggle, Textarea, Select, TimePicker, RepeatableGroup, PlaceSearch } from "./FormComponents";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { motion, AnimatePresence } from "framer-motion";

// Fix Leaflet marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const MapViewUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
            if (center[0] && center[1]) {
                map.flyTo(center, 17, { duration: 1.5 });
            }
        }, 100);
    }, [center, map]);
    return null;
};

if (typeof document !== 'undefined') {
    const styleId = 'map-filters-style';
    if (!document.getElementById(styleId)) {
        const s = document.createElement('style');
        s.id = styleId;
        s.textContent = `
            .dark-map-tiles .leaflet-tile-container { filter: brightness(1.6) contrast(1.2); }
            .light-map-tiles .leaflet-tile-container { filter: grayscale(1) contrast(2.5) brightness(1.1); }
        `;
        document.head.appendChild(s);
    }
}

// Haversine distance in meters
const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const getWalkingTime = (meters) => {
    const minutes = Math.round(meters / 80);
    if (minutes < 1) return 'Less than 1 min walk';
    if (minutes === 1) return '1 min walk';
    return `${minutes} min walk`;
};

export const Step6 = ({ data, updateData, isLight, showValidation, propertyLocation }) => {
  const handleChange = (field, value) => {
    updateData("luggageStorage", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("luggageStorage", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleLockerUpdate = (index, field, value) => {
    const newLockers = [...data.nearbyLockers.lockers];
    newLockers[index] = { ...newLockers[index], [field]: value };
    handleNestedChange("nearbyLockers", "lockers", newLockers);
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Luggage Storage" icon={Briefcase} subtitle="Options for guests to store luggage before check-in or after check-out." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Luggage storage available at property?" 
            value={data.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.beforeCheckIn = { available: false, location: "", from: "08:00", until: "15:00", instructions: "" };
                updates.afterCheckOut = { available: false, location: "", from: "11:00", until: "18:00", instructions: "" };
              }
              updateData("luggageStorage", { ...data, ...updates });
            }}
          />

          {data.available && (
            <div className="flex flex-col gap-8 pl-6 border-l-2 border-white/5">
              <Toggle 
                label="Is luggage storage available before check-in?" 
                value={data.beforeCheckIn.available}
                isLight={isLight}
                onChange={(val) => {
                  const updates = { available: val };
                  if (!val) {
                    updates.location = "";
                    updates.from = "08:00";
                    updates.until = "15:00";
                    updates.instructions = "";
                  } else {
                    if (!data.beforeCheckIn.from) updates.from = "08:00";
                    if (!data.beforeCheckIn.until) updates.until = "15:00";
                  }
                  updateData("luggageStorage", {
                    ...data,
                    beforeCheckIn: { ...data.beforeCheckIn, ...updates }
                  });
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input 
                    label="Storage location" placeholder="Reception, 1st floor" required
                    value={data.beforeCheckIn.location}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("beforeCheckIn", "location", e.target.value)}
                  />
                  <div className="flex gap-2">
                    <TimePicker 
                      label="From" 
                      required
                      value={data.beforeCheckIn.from || "08:00"}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleNestedChange("beforeCheckIn", "from", val)}
                    />
                    <TimePicker 
                      label="Until" 
                      required
                      value={data.beforeCheckIn.until || "15:00"}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleNestedChange("beforeCheckIn", "until", val)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Textarea 
                      label="How should guests drop off their luggage?" placeholder="Leave luggage with the concierge..." required
                      value={data.beforeCheckIn.instructions}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(e) => handleNestedChange("beforeCheckIn", "instructions", e.target.value)}
                    />
                  </div>
                </div>
              </Toggle>

              <Toggle 
                label="Is luggage storage available after check-out?" 
                value={data.afterCheckOut.available}
                isLight={isLight}
                onChange={(val) => {
                  const updates = { available: val };
                  if (!val) {
                    updates.location = "";
                    updates.from = "11:00";
                    updates.until = "18:00";
                    updates.instructions = "";
                  } else {
                    if (!data.afterCheckOut.from) updates.from = "11:00";
                    if (!data.afterCheckOut.until) updates.until = "18:00";
                  }
                  updateData("luggageStorage", {
                    ...data,
                    afterCheckOut: { ...data.afterCheckOut, ...updates }
                  });
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input 
                    label="Storage location" placeholder="Same as check-in" required
                    value={data.afterCheckOut.location}
                    isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("afterCheckOut", "location", e.target.value)}
                  />
                  <div className="flex gap-2">
                    <TimePicker 
                      label="From" 
                      required
                      value={data.afterCheckOut.from || "11:00"}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleNestedChange("afterCheckOut", "from", val)}
                    />
                    <TimePicker 
                      label="Until" 
                      required
                      value={data.afterCheckOut.until || "18:00"}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(val) => handleNestedChange("afterCheckOut", "until", val)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Textarea 
                      label="How should guests pick up their luggage?" placeholder="Leave luggage in the designated area..." required
                      value={data.afterCheckOut.instructions}
                      isLight={isLight}
                      showValidation={showValidation}
                      onChange={(e) => handleNestedChange("afterCheckOut", "instructions", e.target.value)}
                    />
                  </div>
                </div>
              </Toggle>
            </div>
          )}
        </div>
      </Card>

      <Card title="Nearby Lockers" icon={Lock} subtitle="Let guests know about luggage lockers in the area." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Are there nearby luggage lockers?" 
            value={data.nearbyLockers.available}
            isLight={isLight}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) {
                updates.lockers = [];
              }
              updateData("luggageStorage", {
                ...data,
                nearbyLockers: { ...data.nearbyLockers, ...updates }
              });
            }}
          >
            <RepeatableGroup 
              label="Lockers"
              items={data.nearbyLockers.lockers}
              isLight={isLight}
              required={true}
              showValidation={showValidation}
              onAdd={() => {
                const newLockers = [...data.nearbyLockers.lockers, { id: Date.now().toString(), name: "", address: "", hours: "", lat: null, lon: null, notes: "" }];
                handleNestedChange("nearbyLockers", "lockers", newLockers);
              }}
              onRemove={(index) => {
                const newLockers = data.nearbyLockers.lockers.filter((_, i) => i !== index);
                handleNestedChange("nearbyLockers", "lockers", newLockers);
              }}
              renderItem={(locker, index) => (
                <div className="flex flex-col gap-6">
                  <PlaceSearch 
                    label="Search for locker business" 
                    value={locker.name}
                    locationBias={propertyLocation}
                    placeholder="Search by name or address..."
                    isLight={isLight}
                    onSelect={(place) => {
                      const newLockers = [...data.nearbyLockers.lockers];
                      let distance = '';
                      if (propertyLocation?.lat && propertyLocation?.lon && place.lat && place.lon) {
                        const meters = haversineDistance(propertyLocation.lat, propertyLocation.lon, place.lat, place.lon);
                        distance = getWalkingTime(meters);
                      }
                      newLockers[index] = { 
                        ...newLockers[index], 
                        name: place.name, 
                        address: place.address, 
                        lat: place.lat, 
                        lon: place.lon,
                        hours: place.hours || "",
                        distance,
                      };
                      handleNestedChange("nearbyLockers", "lockers", newLockers);
                    }}
                  />
                  
                  <div className={`h-64 w-full rounded-2xl overflow-hidden border relative z-0 ${isLight ? "border-neutral-200" : "border-white/10"}`}>
                    <MapContainer 
                      center={[locker.lat || 37.9838, locker.lon || 23.7275]} 
                      zoom={locker.lat ? 17 : 13} 
                      scrollWheelZoom={true} 
                      style={{ height: '100%', width: '100%' }}
                      className={isLight ? 'light-map-tiles' : 'dark-map-tiles'}
                    >
                      {isLight ? (
                        <TileLayer
                          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                          minZoom={0} maxZoom={20}
                        />
                      ) : (
                        <TileLayer
                          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                          minZoom={0} maxZoom={20}
                        />
                      )}
                      {locker.lat && locker.lon && <Marker position={[locker.lat, locker.lon]} />}
                      <MapViewUpdater center={[locker.lat || 37.9838, locker.lon || 23.7275]} />
                    </MapContainer>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input 
                      label="Locker Name" required
                      value={locker.name} isLight={isLight}
                      showValidation={showValidation}
                      onChange={(e) => handleLockerUpdate(index, "name", e.target.value)}
                    />
                    <Input 
                      label="Address" required
                      value={locker.address} isLight={isLight}
                      showValidation={showValidation}
                      onChange={(e) => handleLockerUpdate(index, "address", e.target.value)}
                    />
                    <Input 
                      label="Business Hours" 
                      placeholder="Mon-Sun: 08:00 - 20:00" 
                      value={locker.hours} isLight={isLight}
                      onChange={(e) => handleLockerUpdate(index, "hours", e.target.value)}
                    />
                    <div className="flex flex-col gap-1">
                      <Input 
                        label="Walking Distance" 
                        placeholder="e.g. 5 min walk" 
                        value={locker.distance || ''} isLight={isLight}
                        onChange={(e) => handleLockerUpdate(index, "distance", e.target.value)}
                      />
                      {locker.distance && (
                        <p className={`text-xs mt-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                          Auto-calculated from property location
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <Textarea
                        label="Instructions / Notes"
                        placeholder="Ground floor, enter from the main entrance. Ask staff if you need help..."
                        value={locker.notes} isLight={isLight}
                        onChange={(e) => handleLockerUpdate(index, "notes", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            />
          </Toggle>
        </div>
      </Card>
    </div>
  );
};
